import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, GitBranch } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

const TRIGGER_TYPES = ["keyword", "sentiment", "after_hours", "max_turns"];
const ACTIONS = ["transfer_human", "send_sms", "voicemail", "callback"];

const empty = { name: "", trigger_type: "keyword", trigger_value: "", action: "transfer_human", target: "", enabled: true };

export default function Escalations() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { const r = await api.get("/rules"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(empty); };
  const startEdit = (it) => { setEditing(it.id); setForm({ ...it }); };
  const cancel = () => setEditing(null);

  const save = async () => {
    const payload = { ...form };
    if (editing === "new") await api.post("/rules", payload);
    else await api.put(`/rules/${editing}`, payload);
    setEditing(null);
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("OK?")) return;
    await api.delete(`/rules/${id}`);
    await load();
  };

  const toggle = async (it) => {
    await api.put(`/rules/${it.id}`, { ...it, enabled: !it.enabled });
    await load();
  };

  return (
    <div className="space-y-6" data-testid="rules-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="chip"><GitBranch className="h-3 w-3" /> {t("rules.title")}</div>
          <h1 className="mt-3 text-3xl lg:text-4xl font-extrabold text-white tracking-tight">{t("rules.title")}</h1>
          <p className="mt-1 text-slate-400 text-sm">{t("rules.sub")}</p>
        </div>
        <button onClick={startNew} className="btn-primary inline-flex items-center gap-2" data-testid="rule-add"><Plus className="h-4 w-4" /> {t("rules.add")}</button>
      </div>

      {editing && (
        <div className="glass rounded-2xl p-6 space-y-4 glow-border" data-testid="rule-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{t("rules.name")}</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark mt-1.5" data-testid="rule-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{t("rules.trigger")}</label>
              <select value={form.trigger_type} onChange={(e) => setForm({ ...form, trigger_type: e.target.value })} className="input-dark mt-1.5" data-testid="rule-trigger">
                {TRIGGER_TYPES.map((tt) => <option key={tt} value={tt}>{t(`rules.types.${tt}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{t("rules.value")}</label>
              <input value={form.trigger_value} onChange={(e) => setForm({ ...form, trigger_value: e.target.value })} className="input-dark mt-1.5" data-testid="rule-value" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{t("rules.action")}</label>
              <select value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} className="input-dark mt-1.5" data-testid="rule-action">
                {ACTIONS.map((a) => <option key={a} value={a}>{t(`rules.actions.${a}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{t("rules.target")}</label>
              <input value={form.target || ""} onChange={(e) => setForm({ ...form, target: e.target.value })} className="input-dark mt-1.5" data-testid="rule-target" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="h-4 w-4 accent-magenta" data-testid="rule-enabled" />
                <span className="text-sm text-slate-300">{t("rules.enabled")}</span>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={cancel} className="btn-ghost inline-flex items-center gap-2" data-testid="rule-cancel"><X className="h-4 w-4" /> {t("kb.cancel")}</button>
            <button onClick={save} className="btn-primary inline-flex items-center gap-2" data-testid="rule-save"><Save className="h-4 w-4" /> {t("kb.save")}</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.length === 0 && <div className="glass rounded-2xl p-10 text-center text-slate-500">{t("rules.empty")}</div>}
        {items.map((r) => (
          <div key={r.id} className="glass rounded-2xl p-5 flex items-center gap-4 flex-wrap" data-testid={`rule-item-${r.id}`}>
            <button onClick={() => toggle(r)} className={`h-3 w-3 rounded-full transition ${r.enabled ? "bg-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.6)]" : "bg-slate-600"}`} data-testid={`rule-toggle-${r.id}`} />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-white">{r.name}</div>
              <div className="text-xs text-slate-400 mt-1">
                <span className="font-mono text-magenta">{t(`rules.types.${r.trigger_type}`)}</span> = <span className="font-mono">{r.trigger_value}</span>
                <span className="text-slate-600 mx-2">→</span>
                <span className="font-mono text-cyan-accent">{t(`rules.actions.${r.action}`)}</span>
                {r.target && <span className="font-mono text-slate-400"> · {r.target}</span>}
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => startEdit(r)} className="p-2 rounded-lg border border-white/5 hover:border-primary/40 text-slate-400 hover:text-white transition" data-testid={`rule-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(r.id)} className="p-2 rounded-lg border border-white/5 hover:border-rose-400/40 text-slate-400 hover:text-rose-400 transition" data-testid={`rule-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
